import React, { useMemo, useRef, useState } from 'react'
import moment from 'moment-timezone'
import { toast } from 'react-toastify'
import CustomStore from 'devextreme/data/custom_store'
import {
  Column,
  Paging,
  Pager,
  LoadPanel,
  RemoteOperations
} from 'devextreme-react/data-grid'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search.svg'
import DataGrid from 'presentation/shared/components/DataGrid'
import { TooltipNew } from 'presentation/shared/components/TooltipNew'
import { PaginationModel } from 'domain/entities/pagination-model'
import { Procedure } from 'presentation/doctor/pages/Patient'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { useServices } from 'presentation/hooks/use-services'
// import { useStores } from 'presentation/hooks/use-stores'
import {
  SurgicalScheduleModel,
  AppointmentModel
} from 'domain/entities/surgical-schedule-model'
import { ReactComponent as InternalIcon } from 'presentation/assets/icons/internal-icon.svg'
import { ReactComponent as UrgencyIcon } from 'presentation/assets/icons/urgency-icon.svg'
import * as S from './styles'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import ScheduleDetailsModal from 'presentation/hospital/components/ScheduleDetailsModal'

type Props = {
  currentHospitalId: number
} & WithLoadingProps

const CancelledSchedulledDataGrid = WithLoading(
  ({ currentHospitalId, setIsLoading }: Props) => {
    const surgicalScheduleService = useServices().surgicalSchedule
    const filtersRef = useRef<any>(null)
    const inputSearchPanelRef = useRef<any>(null)
    const dataGridRef = useRef<any>(null)
    const allowedPageSizes = [5, 10, 15, 20, 30]
    const [paginationState, setPaginationState] = useState<PaginationModel>({
      currentPage: 1,
      itemsPerPage: 20,
      totalItems: 1,
      totalPages: 1
    })
    const [schedule, setSchedule] = useState({}) as any
    const [showModalDetails, setShowModalDetails] = useState<boolean>(false)

    const handleSearchSubmit = (e: any) => {
      e.preventDefault()
      if (!inputSearchPanelRef?.current?.value) return
      setPaginationState({
        ...paginationState,
        currentPage: 1
      })
      dataGridRef.current.instance.pageIndex(0)
    }

    const handleClearFilters = (e: any) => {
      e.preventDefault()
      filtersRef.current.reset()
      setPaginationState({
        ...paginationState,
        currentPage: 1
      })
      dataGridRef.current.instance.pageIndex(0)
    }

    const handlePaginationChange = (currentPage: number) => {
      setPaginationState({ ...paginationState, currentPage: currentPage + 1 })
    }

    const handlePageSizeChange = (pageSize: number) => {
      setPaginationState({
        ...paginationState,
        currentPage: 1,
        itemsPerPage: pageSize
      })
      dataGridRef.current.instance.pageIndex(0)
    }

    const onRowClick = (dataFromArray: any) => {
      const item = dataFromArray.values[1].props.item
      getSchedule(item.id)
    }

    const BoxDefaultCenteredRender = (item: any) =>
      item?.value && <S.BoxDefaultCentered>{item.value}</S.BoxDefaultCentered>

    const getMainProcedureName = (procedures: Procedure[]) =>
      procedures?.filter((procedure) => procedure.isMain)[0].description

    const getSchedule = async (id: number) => {
      try {
        setIsLoading(true)
        const response =
          await surgicalScheduleService.loadCancelledSurgicalScheduleRequest({
            scheduling_id: id
          })
        setShowModalDetails(true)
        setSchedule(response.data)
      } catch (error) {
        toast.error('Falha ao buscar informações da solicitação')
      } finally {
        setIsLoading(false)
      }
    }

    const loadFormattedData = async () => {
      try {
        setIsLoading(true)
        const response = await surgicalScheduleService.loadSurgicalSchedules({
          hospitalId: Number(currentHospitalId),
          status: 'analyze-canceled',
          pageNumber: paginationState.currentPage,
          pageSize: paginationState.itemsPerPage,
          search: inputSearchPanelRef?.current?.value || undefined
        })

        return {
          data: response?.data?.items?.map((item: SurgicalScheduleModel) => ({
            patient_urgency: <UrgencyComponent item={item} />,
            patient_internal: <PatientInternal item={item} />,
            surgical_order_id: item.surgical_order.id,
            createdAt: moment(item.createdAt).format('DD/MM/YYYY - HH:mm'),
            procedure: getMainProcedureName(item?.surgical_order?.procedures),
            health_insurance: item.surgical_order.health_insurance.name,
            patient_name: item.patient.name,
            doctor_name: item.doctor.name,
            scheduled_by_name: item.scheduled_by_name,
            cancel_solicitation_by_name: `${
              item.cancel_solicitation_by_name
            } - ${moment(item.cancel_solicitation_date).format(
              'DD/MM/YYYY - HH:mm'
            )}`
          })),
          totalCount: response?.data?.pageInfo?.totalItems
        }
      } catch (error) {
        toast.error('Não foi possível buscar as solicitações.')
      } finally {
        setIsLoading(false)
      }
    }

    const customStore = new CustomStore({
      key: 'surgical_order_id',
      load: loadFormattedData
    })

    const memoDataGrid = useMemo(() => {
      return (
        <>
          <DataGrid
            // keyExpr="surgical_order_id"
            ref={dataGridRef}
            dataSource={customStore}
            onRowClick={onRowClick}
          >
            <RemoteOperations
              paging={true}
              // summary={false}
              // grouping={false}
              // groupPaging={false}
              // filtering={false}
            />
            <Column
              caption=""
              dataField="patient_internal"
              alignment="left"
              allowSorting={false}
              allowFiltering={false}
              cellRender={BoxDefaultCenteredRender}
            />
            <Column
              caption=""
              dataField="patient_urgency"
              alignment="left"
              allowFiltering={false}
              allowSorting={false}
              cellRender={BoxDefaultCenteredRender}
            />
            <Column
              caption="Nº aviso"
              dataField="surgical_order_id"
              alignment="center"
              allowSearch={false}
              allowSorting={false}
              allowFiltering={false}
              width="80px"
            />
            <Column
              caption="Solicitação criada em"
              dataField="createdAt"
              alignment="center"
              allowFiltering={false}
              allowSorting={false}
              width="175px"
            />
            <Column
              caption="Cirurgia"
              dataField="procedure"
              alignment="center"
              allowFiltering={false}
              allowSorting={false}
              cellRender={BoxDefaultCenteredRender}
            />
            <Column
              caption="Convênio"
              dataField="health_insurance"
              alignment="center"
              allowFiltering={false}
              allowSorting={false}
              cellRender={BoxDefaultCenteredRender}
            />
            <Column
              caption="Paciente"
              dataField="patient_name"
              alignment="center"
              allowFiltering={false}
              allowSorting={false}
              cellRender={BoxDefaultCenteredRender}
            />
            <Column
              caption="Médico (a)"
              dataField="doctor_name"
              alignment="center"
              allowFiltering={false}
              allowSorting={false}
              cellRender={BoxDefaultCenteredRender}
            />
            <Column
              caption="Solicitado por"
              dataField="requested_by_name"
              alignment="left"
              allowFiltering={false}
              allowSorting={false}
              cellRender={BoxDefaultCenteredRender}
            />
            <Column
              caption="Cancelado por"
              dataField="cancel_solicitation_by_name"
              alignment="left"
              allowFiltering={false}
              allowSorting={false}
              cellRender={BoxDefaultCenteredRender}
            />
            <Paging
              enabled={true}
              defaultPageSize={paginationState?.itemsPerPage}
              // defaultPageIndex={paginationState?.currentPage - 1}
              // pageIndex={paginationState?.currentPage - 1}
              onPageIndexChange={handlePaginationChange}
              onPageSizeChange={handlePageSizeChange}
            />
            <Pager
              visible={true}
              showPageSizeSelector={true}
              allowedPageSizes={allowedPageSizes}
              showNavigationButtons={true}
              showInfo={true}
              infoText="Página {0} de {1} ({2} itens)"
            />
            <LoadPanel enabled={false} />
          </DataGrid>
        </>
      )
    }, [paginationState, setPaginationState, currentHospitalId])

    return (
      <>
        <S.FiltersWrapper ref={filtersRef} onSubmit={handleSearchSubmit}>
          <TextFieldNew
            id="search_input"
            placeholder="Nome do paciente ou N° do aviso"
            style={{ width: '100%', minWidth: '320px', maxWidth: '420px' }}
            icon={<SearchIcon />}
            iconIsButton={true}
            onIconClick={handleSearchSubmit}
            ref={inputSearchPanelRef}
          />
          {inputSearchPanelRef?.current?.value && (
            <ButtonNew type="button" size="large" onClick={handleClearFilters}>
              Limpar
            </ButtonNew>
          )}
        </S.FiltersWrapper>

        {memoDataGrid}

        <ScheduleDetailsModal
          title="Solicitação de agendamento cancelado"
          showModalDetails={showModalDetails}
          setShowModalDetails={setShowModalDetails}
          schedule={schedule}
        />
      </>
    )
  }
)

export default CancelledSchedulledDataGrid

type SurgicalScheduleItemProps = {
  item: SurgicalScheduleModel
}

const UrgencyComponent = ({ item }: SurgicalScheduleItemProps) => {
  const solicitedSurgeryDate = moment
    .utc(
      item.appointments?.filter((item: AppointmentModel) => !!item.is_main)[0]
        ?.start_date
    )
    .format('DD/MM/YYYY - HH:mm')

  return item.surgical_order.patient_urgency ? (
    <TooltipNew
      text={`Cirurgia de urgência. Solicitada para o dia ${solicitedSurgeryDate}`}
    >
      <UrgencyIcon />
    </TooltipNew>
  ) : (
    <></>
  )
}

const PatientInternal = ({ item }: SurgicalScheduleItemProps) =>
  item.surgical_order.patient_internal ? (
    <TooltipNew text="Paciente internado">
      <InternalIcon />
    </TooltipNew>
  ) : (
    <></>
  )
